
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { toast } from '@/components/ui/use-toast';

export interface NouvelleChecklist {
  id: string;
  user_id: string;
  bateau_id: string;
  nom: string;
  type: 'Nettoyage' | 'Départ' | 'Arrivée' | 'Hivernage' | 'Personnalisé';
  created_at: string;
  updated_at: string;
}

export interface Tache {
  id: string;
  checklist_id: string;
  tache: string;
  etat: boolean;
  ordre: number;
  created_at: string;
  updated_at: string;
}

export interface ChecklistWithTaches extends NouvelleChecklist {
  taches: Tache[];
}

export const useNouvellesChecklists = () => {
  const [checklists, setChecklists] = useState<ChecklistWithTaches[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const fetchChecklists = async () => {
    if (!user) {
      setChecklists([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Les checklists ne sont plus filtrées par bateau, elles sont communes à l'utilisateur
      const { data: checklistsData, error: checklistsError } = await supabase
        .from('checklists_nouvelles')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (checklistsError) {
        console.error('Erreur lors du chargement des checklists:', checklistsError);
        toast({
          title: "Erreur",
          description: "Impossible de charger vos checklists.",
          variant: "destructive",
        });
        setChecklists([]);
        return;
      }

      const { data: tachesData, error: tachesError } = await supabase
        .from('taches')
        .select('*')
        .in('checklist_id', (checklistsData || []).map(c => c.id))
        .order('ordre', { ascending: true });

      if (tachesError) {
        console.error('Erreur lors du chargement des tâches:', tachesError);
        toast({
          title: "Erreur",
          description: "Impossible de charger les tâches.",
          variant: "destructive",
        });
        setChecklists([]);
        return;
      }

      const checklistsWithTaches = (checklistsData || []).map(checklist => ({
        ...checklist,
        taches: (tachesData || []).filter(tache => tache.checklist_id === checklist.id)
      })) as ChecklistWithTaches[];

      setChecklists(checklistsWithTaches);
    } catch (err) {
      console.error('Erreur:', err);
      setChecklists([]);
    } finally {
      setLoading(false);
    }
  };

  const creerChecklist = async (nom: string, type: string, bateauId: string, taches: string[]) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      // Pour les checklists communes, on utilise toujours le premier bateau ou un ID générique
      const { data: checklistData, error: checklistError } = await supabase
        .from('checklists_nouvelles')
        .insert([{ user_id: user.id, bateau_id: bateauId, nom, type }])
        .select()
        .single();

      if (checklistError) {
        console.error('Erreur lors de la création de la checklist:', checklistError);
        toast({
          title: "Erreur",
          description: "Impossible de créer la checklist.",
          variant: "destructive",
        });
        return { error: checklistError };
      }

      if (taches.length > 0) {
        const tachesData = taches.map((tache, index) => ({
          checklist_id: checklistData.id,
          tache: tache.trim(),
          ordre: index + 1
        }));

        const { error: tachesError } = await supabase
          .from('taches')
          .insert(tachesData);

        if (tachesError) {
          console.error('Erreur lors de la création des tâches:', tachesError);
          await supabase.from('checklists_nouvelles').delete().eq('id', checklistData.id);
          toast({
            title: "Erreur",
            description: "Impossible de créer les tâches.",
            variant: "destructive",
          });
          return { error: tachesError };
        }
      }

      await fetchChecklists();
      toast({
        title: "Succès",
        description: "Checklist créée avec succès !",
      });
      return { data: checklistData };
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const modifierChecklist = async (id: string, nom: string, taches: string[]) => {
    if (!user) return { error: 'Utilisateur non connecté' };

    try {
      // Mettre à jour le nom de la checklist
      const { error: checklistError } = await supabase
        .from('checklists_nouvelles')
        .update({ nom })
        .eq('id', id)
        .eq('user_id', user.id);

      if (checklistError) {
        console.error('Erreur lors de la modification de la checklist:', checklistError);
        toast({
          title: "Erreur",
          description: "Impossible de modifier la checklist.",
          variant: "destructive",
        });
        return { error: checklistError };
      }

      // Supprimer toutes les anciennes tâches
      const { error: deleteError } = await supabase
        .from('taches')
        .delete()
        .eq('checklist_id', id);

      if (deleteError) {
        console.error('Erreur lors de la suppression des anciennes tâches:', deleteError);
        toast({
          title: "Erreur",
          description: "Impossible de supprimer les anciennes tâches.",
          variant: "destructive",
        });
        return { error: deleteError };
      }

      // Ajouter les nouvelles tâches
      if (taches.length > 0) {
        const tachesData = taches.map((tache, index) => ({
          checklist_id: id,
          tache: tache.trim(),
          ordre: index + 1
        }));

        const { error: tachesError } = await supabase
          .from('taches')
          .insert(tachesData);

        if (tachesError) {
          console.error('Erreur lors de la création des nouvelles tâches:', tachesError);
          toast({
            title: "Erreur",
            description: "Impossible de créer les nouvelles tâches.",
            variant: "destructive",
          });
          return { error: tachesError };
        }
      }

      await fetchChecklists();
      toast({
        title: "Succès",
        description: "Checklist modifiée avec succès !",
      });
      return { success: true };
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const modifierTache = async (tacheId: string, etat: boolean) => {
    try {
      const { error } = await supabase
        .from('taches')
        .update({ etat })
        .eq('id', tacheId);

      if (error) {
        console.error('Erreur lors de la modification de la tâche:', error);
        toast({
          title: "Erreur",
          description: "Impossible de modifier la tâche.",
          variant: "destructive",
        });
        return { error };
      }

      // Mise à jour optimiste - pas de rechargement complet
      setChecklists(prevChecklists => 
        prevChecklists.map(checklist => ({
          ...checklist,
          taches: checklist.taches.map(tache => 
            tache.id === tacheId ? { ...tache, etat } : tache
          )
        }))
      );

      return { success: true };
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const supprimerChecklist = async (checklistId: string) => {
    try {
      const { error } = await supabase
        .from('checklists_nouvelles')
        .delete()
        .eq('id', checklistId);

      if (error) {
        console.error('Erreur lors de la suppression:', error);
        toast({
          title: "Erreur",
          description: "Impossible de supprimer la checklist.",
          variant: "destructive",
        });
        return { error };
      }

      await fetchChecklists();
      toast({
        title: "Succès",
        description: "Checklist supprimée avec succès !",
      });
      return { success: true };
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  const reinitialiserChecklist = async (checklistId: string) => {
    try {
      const { error } = await supabase
        .from('taches')
        .update({ etat: false })
        .eq('checklist_id', checklistId);

      if (error) {
        console.error('Erreur lors de la réinitialisation:', error);
        toast({
          title: "Erreur",
          description: "Impossible de réinitialiser la checklist.",
          variant: "destructive",
        });
        return { error };
      }

      await fetchChecklists();
      toast({
        title: "Succès",
        description: "Checklist réinitialisée avec succès !",
      });
      return { success: true };
    } catch (err) {
      console.error('Erreur:', err);
      return { error: err };
    }
  };

  useEffect(() => {
    fetchChecklists();
  }, [user]);

  return {
    checklists,
    loading,
    creerChecklist,
    modifierChecklist,
    modifierTache,
    supprimerChecklist,
    reinitialiserChecklist,
    refetch: fetchChecklists
  };
};
